import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchMenuId, fetchMenuMap } from '../../../redux/actions/posMenu';

/**
 * Loads the POS menu map once if the store doesn't have it yet.
 * Returns { menuMap, loadStatus: 'loading' | 'ready' | 'error', retry }.
 * A failed load isn't retried automatically; `retry` starts a new one.
 */
const usePosMenuMap = (businessId) => {
  const dispatch = useDispatch();
  const { menuMap, menuId } = useSelector((state) => state.posMenu);
  const [loadFailed, setLoadFailed] = useState(false);
  // fetchMenuId stores menuId, which re-runs the effect while the map is still
  // in flight; this keeps that from starting a second fetch
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!businessId) {
      toast.error('No business selected');
      return;
    }
    if (menuMap || loadFailed || inFlightRef.current) return;

    inFlightRef.current = true;
    const load = menuId
      ? dispatch(fetchMenuMap(menuId))
      : dispatch(fetchMenuId(businessId)).then((fetchedMenuId) =>
          dispatch(fetchMenuMap(fetchedMenuId))
        );
    load
      .catch(() => {
        setLoadFailed(true);
        toast.error('Failed to load menu data');
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [dispatch, businessId, menuId, menuMap, loadFailed]);

  const retry = useCallback(() => setLoadFailed(false), []);

  let loadStatus = 'loading';
  if (menuMap) loadStatus = 'ready';
  else if (loadFailed) loadStatus = 'error';

  return { menuMap, loadStatus, retry };
};

export default usePosMenuMap;
